"use client";

import { FC, useEffect, useState } from "react";
import { Radio, RadioGroup } from "@headlessui/react";
import { COL_COLOR_MAPPING } from "../InteractiveTable";
import cn from "classnames";
import { getDataAutomationGroup } from "@/helpers";
import { CheckMark, Star } from "@/icons";
import { DialogModal } from "@/components/Modal";
import { ComponentWithAnalyticsGlobalField } from "@/types";
import { Button } from "@/components/Button";

export const ICON_MAPPING = {
  checkmark: <CheckMark className="text-swdc-black-600 h-4 w-4 font-bold" />,
  star: <Star className="text-swdc-orange-400 font-bold" />,
} as const;

export interface InteractiveTableCellProps {
  header: string;
  verticalCategory: {
    categoryGroup: string;
    category: string;
  };
  horizontalCategory: {
    categoryGroup: string;
    category: string;
    colIndex: number;
  };
  description: string;
  selection: {
    header: string;
    options: {
      icon: keyof typeof ICON_MAPPING;
      description: string;
    }[];
    ctaText: string;
    analytics?: ComponentWithAnalyticsGlobalField;
  };
  appearance: "highlighted" | "default" | "disabled";
  setValid: (arg: boolean) => void;
  resetCounter?: number;
}

const APPEARANCE_STYLES_MAPPING = {
  default: "",
  highlighted: "border border-b-[3px] border-r-[3px] border-swdc-black-600",
  disabled: "!bg-[#fdfdfd] !ring-0 before:hidden shadow-none",
};

const InteractiveTableCell: FC<InteractiveTableCellProps> = ({
  header,
  description,
  verticalCategory,
  horizontalCategory,
  selection,
  appearance,
  setValid,
  resetCounter,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeOption, setActiveOption] = useState<number | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  const openDialog = () => {
    setActiveOption(selectedOption);
    setIsOpen(true);
  };
  const closeDialog = () => setIsOpen(false);

  const onSave = () => {
    setSelectedOption(activeOption);
    setValid(typeof activeOption === "number");
    closeDialog();
  };

  useEffect(() => {
    setActiveOption(null);
    setSelectedOption(null);
  }, [resetCounter]);

  const selectedIcon =
    selectedOption !== null && selection?.options[selectedOption]?.icon;

  return (
    <>
      <button
        className={cn(
          "text-link-s text-swdc-black-600 relative flex size-full items-center justify-center p-4 text-center font-medium underline ring-[0.4375rem] transition-all ring-inset hover:-translate-y-1 hover:shadow-[6px_6px_10px_0_rgba(0,0,0,0.16)]",
          APPEARANCE_STYLES_MAPPING[appearance],
          COL_COLOR_MAPPING[horizontalCategory.colIndex]?.btnColor,
        )}
        onClick={openDialog}
      >
        {header}
        {selectedIcon && (
          <span className="absolute top-2 right-3">
            {ICON_MAPPING[selectedIcon]}
          </span>
        )}
      </button>
      <DialogModal open={isOpen} onClose={closeDialog} maxWidth="5xl">
        <div className="border-b-swdc-silver-200 flex flex-row justify-between border-b-2 pb-6">
          <h2 className="text-subhead-xl text-swdc-black-600 font-bold">
            {header}
          </h2>
          <div className="flex flex-row gap-8">
            <p className="text-body-s text-swdc-gray-300 italic">
              {verticalCategory.categoryGroup}
              {": "}
              <span className="bg-swdc-gray-400 px-2 py-1 font-bold text-white not-italic">
                {verticalCategory.category}
              </span>
            </p>
            <p className="text-body-s text-swdc-gray-300 italic">
              {horizontalCategory.categoryGroup}
              {": "}
              <span
                className={cn(
                  "text-swdc-black-600 px-2 py-1 font-bold not-italic",
                  COL_COLOR_MAPPING[horizontalCategory.colIndex]?.background,
                )}
              >
                {horizontalCategory.category}
              </span>
            </p>
          </div>
        </div>
        <div className="border-b-swdc-silver-200 border-b-2">
          <div
            className="text-body-m text-swdc-gray-400 [&_li]:display-list-item [&_li]:marker:text-swdc-orange-400 [&_ol]:marker:text-swdc-gray-400 mb-6 columns-2 gap-8 pt-6 [&_li]:break-inside-avoid [&_li]:pb-3 [&_li]:last:pb-0 [&_ol]:ml-6 [&_ol]:list-decimal [&_p]:mb-4 [&_p]:break-inside-avoid [&_p]:last:mb-0 [&_strong]:font-bold [&_ul]:ml-4 [&_ul]:list-outside [&_ul]:list-disc [&_ul]:break-inside-avoid"
            dangerouslySetInnerHTML={{ __html: description ?? "" }}
          />
        </div>
        <div className="pt-6">
          <p className="text-subhead-m text-swdc-black-600 font-bold">
            {selection?.header}
          </p>
          <div className="flex flex-row items-center gap-x-14">
            <RadioGroup
              value={activeOption}
              onChange={setActiveOption}
              className="flex flex-row"
            >
              <div className="flex flex-row items-center gap-x-14">
                {selection?.options?.map((option, index) => (
                  <Radio
                    as="div"
                    value={index}
                    key={`radio-button-${index}`}
                    onClick={() => {
                      if (activeOption === index) {
                        setActiveOption(null);
                      }
                    }}
                    className="group text-body-m flex flex-row items-center gap-x-2 hover:cursor-pointer"
                    {...getDataAutomationGroup("radio-button")}
                  >
                    {({ checked }) => (
                      <>
                        <span
                          className={cn(
                            "size-4 rounded-full border",
                            checked
                              ? "border-swdc-blue-500 border-5 bg-white"
                              : "border-swdc-gray-300 group-hover:border-swdc-blue-500 bg-white group-hover:border-2",
                          )}
                        />
                        <span className="text-swdc-black-600 flex items-center justify-center">
                          ( {ICON_MAPPING[option.icon]} )
                        </span>
                        <span className="text-swdc-gray-400">
                          {option.description}
                        </span>
                      </>
                    )}
                  </Radio>
                ))}
              </div>
            </RadioGroup>
            <Button
              variant="primary"
              onClick={onSave}
              dld={selection?.analytics?.dld || ""}
              dlt={selection?.analytics?.dlt || ""}
            >
              {selection?.ctaText}
            </Button>
          </div>
        </div>
      </DialogModal>
    </>
  );
};

export default InteractiveTableCell;
